import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { createTableColumns } from "./TableColumns";
import type { Job, PaginationInfo } from "../../interfaces/apiInterfaces/jobs";
import { useResources } from "../../hooks/useResources";
import "../../assets/styles/sass/modules/_table.scss";

import TableFilter from "./TableFilter";
import TableHeader from "./TableHeader";
import ScrollbarArrows from "./ScrollbarArrows";

interface TableProps {
  jobs: Job[];
  pagination: PaginationInfo | null;
  isLoading: boolean;
  onCSVDownload?: () => void;
  title?: string;
  uploadFilesLabel?: string;
  uploadFilesPath?: string;
  isCSVDisabled?: boolean;
  jobType: "create" | "update";
}

const Table: React.FC<TableProps> = ({ jobs, pagination, isLoading, onCSVDownload, title, uploadFilesLabel, uploadFilesPath, isCSVDisabled, jobType }) => {
  const { getResourceValue } = useResources();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const columns = useMemo(() => createTableColumns(), []);

  const table = useReactTable({
    data: jobs || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true, // Use server-side pagination
    pageCount: pagination?.total_pages ?? 0,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: (pagination?.page_number ?? 1) - 1,
        pageSize: pagination?.page_size ?? 100,
      },
    },
  });

  const hasRows = (jobs || []).length > 0;

  return (
    <section className="table-container">
      <TableHeader 
        title={title || getResourceValue("all_jobs", "All Jobs")}
        uploadFilesLabel={uploadFilesLabel || getResourceValue("upload_files", "Upload Files")}
        onCSVDownload={onCSVDownload}
        uploadFilesPath={uploadFilesPath}
        isCSVDisabled={isCSVDisabled}
      />
      <div className="table-wrapper">
        <TableFilter jobType={jobType} />

        {isLoading ? (
          <div className="loading-table">
            <p>{getResourceValue("loading_jobs", "Loading jobs...")}</p>
          </div>
        ) : (
          <>
            <ScrollbarArrows
              targetId="jobs-table-scroll"
              tablesScroll={columns.length}
            />
            <div className="table-scroll-container" id="jobs-table-scroll">
              <table className="data-table">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers
                        .filter((header) => {
                          if (header.isPlaceholder) return false;
                          const headerContent = header.column.columnDef.header;
                          const isEmpty = !headerContent || headerContent === "";
                          const isJobDetails = header.column.id === "jobDetails";
                          return !isEmpty || isJobDetails;
                        })
                        .map((header) => (
                          <th
                            key={header.id}
                            style={{ width: header.getSize() }}
                            className={header.column.getCanSort() ? "sortable" : ""}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </th>
                        ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>

              {!hasRows && (
                <div className="no-data">
                  <p>{getResourceValue("no_jobs_found", "No jobs found matching your criteria.")}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Table;
